import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { interval, Subject, take, takeUntil } from 'rxjs'
import { MyDialogComponent } from '../../my-dialog/my-dialog.component'
import { RuledMessageModalComponent } from '../ruled-message-modal/ruled-message-modal.component'
import { environment } from 'src/config/environments/environment'
import { ActiveModuleService } from '@app/shared/services/active-module.service'
import { EventsService } from '@app/modules/culture/events/services/events.service'
import { RequestPreviewFieldsRuleBasedMessagePage } from '@app/query/event.query'
import { EventType } from '@app/models/event.model'
import { YabiTableComponent } from '../../yabi-table/yabi-table.component'
import { TableConfig } from '@app/models/shared.model'
import { RuleBasedNotificationsService } from '@app/shared/services/rule-base-messages.service'
import { DetailsService } from '@app/shared/pages/settings/services/details.service'
import { Client } from '@app/models/client.model'
import { Initiator, Target, Trigger, UpdateRuleNotificationInput } from '@app/models/message.model'
import { TranslateService } from '@ngx-translate/core'
import { indexOf, isArray } from 'lodash-es'
import { CommonService } from '@app/shared/services/common.service'

/** Row shape rendered by the rule-based messages table */
interface RuledMessageRow {
  event_id: number | number[]
  target?: Target[]
  initiator?: Initiator[]
  trigger: Trigger[]
}

@Component({
  selector: 'app-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RuleBaseMessageIndexComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef)
  /** Write based-rule message reference for open when click "send new message" button */
  @ViewChild('writeRuleMessage') writeRuleMessage: RuledMessageModalComponent

  /** Ruled messages reference for control search */
  @ViewChild('ruledMessageRowRef') ruledMessageRowRef: YabiTableComponent

  /** Use to open dialog */
  @ViewChild('dialogRef') dialogRef: MyDialogComponent

  /** Is data loaded? */
  loaded: boolean = false

  /** Current rule message (when click edit message)  */
  currentMessage: any

  /** Use has permission */
  hasPermission: boolean = false

  /** Active module name */
  activeModuleName: string

  /** Events list */
  events: any[]

  /** Employees category id */
  employeesCategoryId: number

  /** Authorized category id */
  authorizedCategoryId: number

  /** Rule message config */
  tableConfig: TableConfig = new TableConfig()

  /** Current client ID */
  clientId: number

  /** Trigger types list */
  triggers: any = []

  /** Target types list */
  targets: Target[]

  /** Initiator types list */
  initiators: Initiator[]

  /** Save the selected row for activate/deactivate */
  selectedRule: UpdateRuleNotificationInput

  requester$ = new Subject()
  sysMessage: any

  constructor(
    private activeModuleService: ActiveModuleService,
    private eventService: EventsService,
    private ruleMessageService: RuleBasedNotificationsService,
    private detailsService: DetailsService,
    private translate: TranslateService,
    private commonService: CommonService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Check if we need to refetch data (component reused on navigation)
    const needsInitialization = !this.authorizedCategoryId

    if (needsInitialization) {
      this.detailsService
        .getCurrentClientBy('id')
        .pipe(take(1))
        .subscribe((cli: Client) => {
          this.clientId = +cli.id
          this.cdr.markForCheck()
        })

      this.ruleMessageService
        .getRuleBasedMessagesDetails()
        .pipe(take(1))
        .subscribe((data: any) => {
          this.targets = data.target
          this.initiators = data.initiator

          for (let i = 0; i < data.trigger.length; i++) {
            this.triggers[i] = { name: data.trigger[i], isDisabled: false }
          }
          this.cdr.markForCheck()
        })

      this.eventService
        .getCategories(true)
        .pipe(take(1))
        .subscribe((categories) => {
          this.activeModuleService.getCurrentModule().then((m: any) => {
            this.activeModuleName = m.value.toLowerCase()

            this.authorizedCategoryId = Number(categories?.find((category: EventType) => category.slug == 'authorized')?.id)
            this.employeesCategoryId = Number(categories?.find((category: EventType) => category.slug == 'employees')?.id)

            this.eventService
              .getEventsBy(
                {
                  category: [Number(categories.find((c: EventType) => c.slug == this.activeModuleName)?.id)],
                },
                RequestPreviewFieldsRuleBasedMessagePage
              )
              .pipe(take(1))
              .subscribe((events: any[]) => {
                this.events = events
                this.onReady()
                this.cdr.markForCheck()
              })
          })
        })
    } else {
      // Component was reused, just ensure UI is up to date
      this.cdr.markForCheck()
      // Refetch messages to ensure fresh data
      this.createRequest()
    }

    this.tableConfig.class = 'p-datatable-sm'
    this.tableConfig.id = 'RULE_BASED_MESSAGES'
    this.tableConfig.columnsConfig = [
      {
        name: 'title',
        label: 'WRITE_MESSAGE.MESSAGE_TITLE',
        type: 'text',
        style: 'min-width: 200px; max-width: 200px',
        sortable: true,
      },
      {
        name: 'event_id',
        label: 'MESSAGES.FORM_LABELS.APPLIES_TO',
        type: 'remainedData',
        sortable: true,
        style: 'min-width:200px',
        remainedDataConfig: {
          key: 'name',
          remainedNumber: 2,
          callbackFunction: (args: RuledMessageRow) => {
            const value = !isArray(args.event_id) ? [args.event_id] : args.event_id

            return this.events?.filter((v: any) => value.includes(+v.id))
          },
        },
      },
      {
        name: 'target',
        label: 'MESSAGES.FORMS.FIELD_LABELS.TARGET',
        type: 'remainedData',
        sortable: true,
        style: 'min-width:200px',
        remainedDataConfig: {
          key: '',
          remainedNumber: 3,
          callbackFunction: (args: RuledMessageRow) => {
            return args.target?.filter((v: Target) => this.targets.includes(v)).map((b: string) => this.translate.instant(`MESSAGES.TARGET.${b}`))
          },
        },
      },
      {
        name: 'initiator',
        label: 'MESSAGES.FORMS.FIELD_LABELS.INITIATORS',
        type: 'remainedData',
        sortable: true,
        style: 'min-width:200px',
        remainedDataConfig: {
          key: '',
          remainedNumber: 3,
          callbackFunction: (args: RuledMessageRow) => {
            return args?.initiator?.filter((v: Initiator) => this.initiators.includes(v)).map((b: string) => this.translate.instant(`MESSAGES.INITIATOR.${b}`))
          },
        },
      },
      {
        name: 'trigger',
        label: 'MESSAGES.FORMS.FIELD_LABELS.TRIGGERS',
        type: 'remainedData',
        sortable: true,
        style: 'min-width:200px',
        remainedDataConfig: {
          key: '',
          remainedNumber: 3,
          callbackFunction: (args: RuledMessageRow) => {
            const triggers = this.triggers.map((v: any) => v.name)
            return args.trigger.filter((v: Trigger) => triggers.includes(v)).map((b: string) => this.translate.instant(`MESSAGES.TRIGGERS.${b}`))
          },
        },
      },
      {
        name: 'platform',
        label: 'MESSAGES.FORMS.FIELD_LABELS.PLATFORM',
        type: 'translated',
        sortable: true,
        translatePrevLabel: 'MESSAGES.PLATFORM.',
      },
    ]

    this.tableConfig.menuOptions = [
      {
        label: 'COMMON.BUTTONS.EDIT',
        icon: 'pi pi-fw pi-pencil',
        iconClass: 'menu-color-primary',
        actionName: 'edit',
      },
    ]

    this.tableConfig.showRowMenuConditionCallback = () => {
      return true
    }
  }

  ngOnDestroy() {
    this.destroyFetch()
  }

  get dir() {
    return document.dir
  }

  destroyFetch() {
    if (!this.requester$.closed) {
      this.requester$?.next(true)
      this.requester$?.unsubscribe()
    }
  }

  onReady() {
    this.createRequest()
  }

  /**
   * Create request method, call fetch messages method every so often as defined
   */
  createRequest() {
    this.destroyFetch()
    this.requester$ = new Subject()
    this.fetchMessages()
    interval(environment.authorizedPeriod)
      .pipe(takeUntil(this.requester$))
      .subscribe(() => {
        this.fetchMessages()
      })
  }

  /**
   * Fetch messages method for update messages array of objects
   */
  fetchMessages() {
    this.ruleMessageService
      .getRuleBaseNotifications()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((messages: UpdateRuleNotificationInput[]) => {
        const similarObjects: any[] = this.commonService.findSimilarObjects(messages, ['id', 'event_id', 'areas', 'active'])

        similarObjects.forEach((v) => {
          const firstObject = v[0]
          const indexOfTheFirst = indexOf(messages, v[0])
          // Update the first object with areas and event_id from others
          v.slice(1).forEach((otherObject: any) => {
            if (otherObject.areas) firstObject.areas = [...(firstObject?.areas ?? []), ...(otherObject?.areas ?? [])]
            firstObject.event_id = isArray(firstObject.event_id) ? [...firstObject.event_id, otherObject.event_id] : [firstObject.event_id, otherObject.event_id]

            firstObject.id = isArray(firstObject.id) ? [...firstObject.id, otherObject.id] : [firstObject.id, otherObject.id]
          })

          // Remove the duplicate objects
          messages.splice(indexOfTheFirst, v[0].length - 1)
        })
        this.tableConfig.data = messages

        this.loaded = true
        this.cdr.markForCheck()
      })
    this.loaded = true
  }

  /**
   * When child component like (write-message) close button click method, call "createRequest" method
   */
  onStartFetchMessages() {
    this.createRequest()
    this.triggers.map((v: any) => (v.isDisabled = false))
  }

  openRuleModalForm(_e: Event) {
    this.writeRuleMessage.open({}, this.events)
  }

  /**
   * When click on messages row action method
   * @param obj Object that contains action and selected model
   */
  toMessageAction(obj: any) {
    const model = obj.model
    this.currentMessage = model

    if (obj.action == 'edit') {
      this.writeRuleMessage.open(model, this.events)
    }
  }
}
